import {
  Box,
  Pagination,
  Icon,
  formattedCurrency,
  usePagination,
  useUpdateQueryParams,
  Timestamp,
  Timezone,
  CircleButton,
  BodyText,
  withStyles,
  createTableProps,
} from 'vopay';
import { FunctionComponent, useEffect, useState, useMemo } from 'react';
import { Title } from 'UI';
import { parseISO, subDays } from 'date-fns';
import { formatInTimeZone } from 'date-fns-tz';
import { useLocation } from 'react-router-dom';
import { useUserSettingsProvider } from '@/providers/UserSettings.provider';
import useFileConversion from '@/hooks/useApi/useFileConversion';
import FileConversionTableFilters from './FileConversionTableFilters';
import styles from './FileUploadHistory.module.scss';
import { Button, TableFiltersContainer, Table } from 'UI';
import { useAlertDialog } from '@/providers/AlertDialog.provider';
import NoRecordSvg from './NoRecord.svg';
import { useTranslation, Trans } from 'react-i18next';
import { format } from 'date-fns';
import { useDataFetchIndicator } from '@/hooks';
import { useToast } from '@/providers/Toast.provider';
import { useClientData } from '@/providers/ClientData.provider';
import { useLockedFeatures } from '@/providers/LockedFeatures.provider';
import { useLockedFeatureDialogue } from '@/providers/LockedFeatureDialogue.provider';
import { ConversionStatus, PaymentRail, Service, FileConversionQueryParams, FileConversionSearchParams } from './types';
const classes = withStyles(styles);

type FC = FunctionComponent;

const FileConversionTable: FC = () => {
  const location = useLocation();
  const {
    response: { Country },
  } = useClientData();
  const { userSettings } = useUserSettingsProvider();
  const {
    history,
    getHistory,
    downloadFile,
    downloadSourceFile,
    downloadLoading,
    submitFile,
    configurations,
    getConfigurations,
  } = useFileConversion();
  const { t } = useTranslation('translations', { keyPrefix: 'FileUpload.History.FileConversionTable' });
  const { t: tGlobal } = useTranslation('translations', { keyPrefix: 'Global' });
  const [, setLoading] = useDataFetchIndicator();
  const alertDialog = useAlertDialog();
  const toast = useToast();
  const { updateIsLocked } = useLockedFeatureDialogue();
  const lockedFeatures = useLockedFeatures();
  const queryParams: FileConversionQueryParams = useMemo(() => {
    const params = new URLSearchParams(location.search);
    return {
      conversionStatus: (params.get('conversionStatus') || '') as ConversionStatus,
      paymentRail: (params.get('paymentRail') || '') as PaymentRail,
      service: (params.get('service') || '') as Service,
      timeZone: params.get('timeZone') || '',
      startDate: params.get('startDate') || '',
      endDate: params.get('endDate') || '',
    };
  }, [location.search]);

  const conversionStatus = useMemo(() => queryParams.conversionStatus || '', [queryParams]);
  const paymentRail = useMemo(() => queryParams.paymentRail || '', [queryParams]);
  const service = useMemo(() => queryParams.service || '', [queryParams]);
  const timeZone = useMemo(() => (queryParams.timeZone as Timezone) || userSettings.timezone || 'UTC', [queryParams]);

  const timeDifference = useMemo(() => formatInTimeZone(subDays(new Date(), 7), timeZone, 'yyyy-MM-dd'), [timeZone]);
  const currentDateTime = useMemo(() => formatInTimeZone(new Date(), timeZone, 'yyyy-MM-dd'), [timeZone]);

  const startDate = useMemo(() => queryParams.startDate || timeDifference, [queryParams, timeDifference]);
  const endDate = useMemo(() => queryParams.endDate || currentDateTime, [queryParams, currentDateTime]);

  const filtersApplied = useMemo(() => {
    return (
      !!conversionStatus ||
      !!paymentRail ||
      !!service ||
      !!(queryParams.timeZone && queryParams.timeZone !== userSettings.timezone) ||
      startDate !== timeDifference ||
      endDate !== currentDateTime
    );
  }, [
    conversionStatus,
    paymentRail,
    service,
    queryParams.timeZone,
    userSettings.timezone,
    startDate,
    endDate,
    timeDifference,
    currentDateTime,
  ]);

  const updateQueryParams = useUpdateQueryParams<FileConversionQueryParams>();

  const [initialLoad, setInitialLoad] = useState(false);
  // Load configurations on mount
  useEffect(() => {
    getConfigurations();
  }, []);

  // Helper function to get bank name from FileType
  const getBankNameFromFileType = (fileType: string): string => {
    const config = configurations.find((c) => c.FileType === fileType);
    if (config) {
      return config.FinancialInstitution;
    }
    return fileType; // Fallback to FileType if not found
  };

  const handleDownload = async (fileConversionID: string, fileType: string) => {
    try {
      setLoading(true);
      await downloadFile({ FileConversionID: fileConversionID, FileType: fileType });
    } catch (err) {
      const msg = err instanceof Error ? err.message : t('downloadError');
      alertDialog.error(t('downloadErrorTitle'), msg);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadSource = async (fileConversionID: string) => {
    try {
      setLoading(true);
      await downloadSourceFile({ FileConversionID: fileConversionID });
    } catch (err) {
      const msg = err instanceof Error ? err.message : t('downloadError');
      alertDialog.error(t('downloadErrorTitle'), msg);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleProcessTransactions = async (fileConversionID: string, fileType: string, currency: string) => {
    const bankName = getBankNameFromFileType(fileType);

    const result = await alertDialog.open(
      t('processDialog.title', {
        bank: bankName,
      }),
      <>
        <BodyText className={classes('processDialogDescription')}>
          {t('processDialog.description', {
            bank: bankName,
          })}
        </BodyText>
        <BodyText className={classes('processDialogDescription')}>{t('processDialog.warning')}</BodyText>
      </>,
      {
        confirmLabel: t('processDialog.confirmLabel'),
        abortLabel: t('processDialog.cancelLabel'),
      },
    );

    if (result !== 'confirm') {
      return;
    }

    try {
      setLoading(true);
      const res = await submitFile({
        FileConversionID: fileConversionID,
      });

      if (!res.Success) {
        throw new Error(res.ErrorMessage);
      }

      // Show success toast if all processed successfully
      if (res.TotalFailed !== 0) {
        toast.success(
          <Trans
            i18nKey="FileUpload.History.FileConversionTable.processSuccess"
            values={{
              count: res.TotalTransactions,
              currency: currency,
              amount: formattedCurrency(res.TotalAmount.toString()),
            }}
            components={{ bold: <strong /> }}
          />,
        );
      } else {
        // Show neutral toast if there were failures
        toast.neutral(
          <Box flx="row" gap="sm">
            <div>
              <Trans
                i18nKey="FileUpload.History.FileConversionTable.processWithFailures"
                values={{
                  count: res.TotalTransactions,
                  currency: currency,
                  amount: formattedCurrency(res.TotalAmount.toString()),
                }}
                components={{ bold: <strong /> }}
              />
            </div>
            <div style={{ width: '1px', height: '24px', background: '#2D2D2D1A' }} />
            <div>
              <Trans
                i18nKey="FileUpload.History.FileConversionTable.processFailureCount"
                values={{
                  failedCount: res.TotalFailed,
                }}
                components={{ bold: <strong /> }}
              />
            </div>
          </Box>,
        );
      }

      const searchParams: Partial<FileConversionSearchParams> = {
        conversionStatus,
        paymentRail,
        service,
        timeZone,
        startDate: startDate,
        endDate: endDate,
      };
      search(offset, searchParams);
    } catch (err) {
      const msg = err instanceof Error ? err.message : t('processFailed');
      alertDialog.error(t('processError'), msg);
    } finally {
      setLoading(false);
    }
  };

  // pagination
  const [totalDataLength, setTotalDataLength] = useState(0);

  const PER_PAGE = 20;
  const { pageCount, pageIndex, offset, setPageIndex } = usePagination(totalDataLength, {
    pageSize: PER_PAGE,
  });

  const search = async (offset = 0, searchParams: Partial<FileConversionSearchParams>) => {
    const defaultValues = {
      ...(searchParams.conversionStatus === 'all' ? {} : { ConversionStatus: searchParams.conversionStatus as any }),
      ...(searchParams.paymentRail === 'all' ? {} : { PaymentRail: searchParams.paymentRail as any }),
      ...(searchParams.service === 'all' ? {} : { Service: searchParams.service as any }),
      Timezone: searchParams.timeZone ?? timeZone,
      StartDate: searchParams.startDate ?? startDate,
      EndDate: searchParams.endDate ?? endDate,
      Limit: PER_PAGE,
      Offset: offset,
    };
    try {
      setLoading(true);
      const res = await getHistory(defaultValues);
      setTotalDataLength(res.PaginationMetaData?.TotalRecords || 0);
      if (!res.Success) throw new Error(res.ErrorMessage);

      setInitialLoad(true);
    } catch (err) {
      const msg = err instanceof Error ? err.message : t('fetchHistoryError');
      alertDialog.error(t('error'), msg);
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const offset = setPageIndex(0);
    const searchParams: Partial<FileConversionSearchParams> = {
      conversionStatus,
      paymentRail,
      service,
      timeZone,
      startDate: startDate || timeDifference,
      endDate: endDate || currentDateTime,
    };
    updateQueryParams({
      conversionStatus,
      paymentRail,
      service,
      timeZone,
      startDate: startDate || timeDifference,
      endDate: endDate || currentDateTime,
    });
    search(offset, searchParams);
  }, []);

  const tableProps = createTableProps({
    id: 'fileConversion-list',
    zebra: true,
    columns: [
      {
        id: 'convertedDate',
        header: t('Table.convertedDate'),
        align: 'left',
        field: 'DateAdded',
      },
      {
        id: 'originalFile',
        header: t('Table.originalFile'),
        align: 'left',
        template: ({ data }) => (
          <Box flx="row" gap="sm" ai="center">
            <span>{data.SourceFileName}</span>
            <CircleButton
              aria-label={t('Table.downloadOriginal')}
              icon="download"
              colorScheme="action"
              size="small"
              onClick={() => handleDownloadSource(data.FileConversionID)}
              disabled={downloadLoading}
            />
          </Box>
        ),
      },
      {
        id: 'convertedFile',
        header: t('Table.convertedFile'),
        align: 'left',
        template: ({ data }) => (
          <Box flx="row" gap="sm" ai="center">
            <span>{data.TargetFileName}</span>
            {data.FileType && (
              <CircleButton
                aria-label={t('Table.downloadConverted')}
                icon="download"
                colorScheme="action"
                size="small"
                onClick={() => handleDownload(data.FileConversionID, data.FileType)}
                disabled={downloadLoading}
              />
            )}
          </Box>
        ),
      },
      {
        id: 'transferType',
        header: t('Table.transferType'),
        align: 'left',
        template: ({ data }) =>
          data.Service === 'Credit' ? tGlobal('ServiceOptions.credit') : tGlobal('ServiceOptions.debit'),
      },
      {
        id: 'paymentRail',
        header: t('Table.paymentRail'),
        align: 'left',
        template: ({ data }) => {
          const rail = data.PaymentRail.toUpperCase();
          return tGlobal(`PaymentRail.${rail}`);
        },
      },
      {
        id: 'numberOfRecords',
        header: t('Table.numberOfRecords'),
        align: 'left',
        template: ({ data }) => data.TransactionCount.toString(),
      },
      {
        id: 'totalAmount',
        header: t('Table.totalAmount'),
        align: 'left',
        template: ({ data }) => `${Country === 'US' ? 'USD' : 'CAD'} ${formattedCurrency(data.TotalAmount)}`,
      },
      {
        id: 'actions',
        header: t('Table.actions'),
        align: 'left',
        template: ({ data }) => {
          const isLocked = !lockedFeatures.FileSubmissionEnabled;
          return (
            <Box flx="col" gapSize="6px" ai="center">
              <Button
                id={`process-transactions-${data.FileConversionID}`}
                size="small"
                colorScheme="secondary"
                iconRight={!lockedFeatures.FileSubmissionEnabled ? 'lock' : undefined}
                onClick={() => {
                  if (isLocked) {
                    updateIsLocked(true, t('Table.fileSubmission'));
                  } else {
                    handleProcessTransactions(data.FileConversionID, data.FileType, Country === 'US' ? 'USD' : 'CAD');
                  }
                }}
                disabled={data.IsSubmitted}
              >
                {t('Table.processTransactions')}
              </Button>
              {data.IsSubmitted && data.SubmittedDate && (
                <div className={classes('submittedOn')}>
                  {t('Table.submittedOn', {
                    date: format(parseISO(data.SubmittedDate), 'yyyy-MM-dd'),
                  })}
                </div>
              )}
            </Box>
          );
        },
      },
    ],
    rows: history.map((file, index) => ({
      id: String(file.FileConversionID),
      data: { ...file, index },
    })),
  });

  return (
    <Box flx="col" gap="sm">
      <TableFiltersContainer filtersApplied={filtersApplied}>
        <Box jc="fe" ai="center" base="8" gap="md">
          <TableFiltersContainer.Trigger />
        </Box>
        <TableFiltersContainer.Content>
          <FileConversionTableFilters
            service={service}
            paymentRail={paymentRail}
            conversionStatus={conversionStatus}
            timeZone={timeZone}
            initialTimeRange={[
              parseISO(startDate || timeDifference).getTime() as Timestamp,
              parseISO(endDate || currentDateTime).getTime() as Timestamp,
            ]}
            onSearch={(searchParams) => {
              const offset = setPageIndex(0);
              const params: Partial<FileConversionSearchParams> = {
                service: searchParams.service,
                paymentRail: searchParams.paymentRail,
                conversionStatus: searchParams.conversionStatus,
                timeZone: searchParams.timeZone,
                startDate: format(new Date(searchParams.timeRange[0]), 'yyyy-MM-dd'),
                endDate: format(new Date(searchParams.timeRange[1]), 'yyyy-MM-dd'),
              };
              updateQueryParams({
                service: searchParams.service,
                paymentRail: searchParams.paymentRail,
                conversionStatus: searchParams.conversionStatus,
                timeZone: searchParams.timeZone,
                startDate: format(new Date(searchParams.timeRange[0]), 'yyyy-MM-dd'),
                endDate: format(new Date(searchParams.timeRange[1]), 'yyyy-MM-dd'),
              });
              search(offset, params);
            }}
          />
        </TableFiltersContainer.Content>
      </TableFiltersContainer>
      <Box>
        <div className={classes('tableFileConversionContainer')}>
          <Table {...tableProps} />
        </div>
      </Box>

      {!initialLoad && (
        <Box flx="col" mt="lg" jc="center">
          <Box ai="center" jc="center">
            <Icon icon="search2" size="lg" />
          </Box>
          <Box ai="center" jc="center" pt="md">
            {t('noSearchPerformed')}
          </Box>
        </Box>
      )}
      {initialLoad && history && history.length === 0 && (
        <Box flx="col" gap="ml" mt="xl" mb="xl" pt="lg" pb="lg" base="8" jc="center">
          <Box ai="center" jc="center">
            <img src={NoRecordSvg} alt="No records" width="73" height="90" />
          </Box>
          <Box flx="col" gap="sm" base="8" jc="center">
            <Title level="3" emphasized style={{ textAlign: 'center' }}>
              {t('noConversionsFound')}
            </Title>
            <div className={classes('noRecordMessageDescription')}>{t('noConversionsDescription')}</div>
          </Box>
        </Box>
      )}
      <Box width="100%" jc="center" mt="lg" mb="sm">
        {pageCount > 1 && (
          <Pagination
            pageCount={pageCount}
            forcePageNumber={pageIndex}
            handlePageClick={(e) => {
              const offset = setPageIndex(e.selected);
              const searchParams: Partial<FileConversionSearchParams> = {
                conversionStatus,
                paymentRail,
                service,
                timeZone,
                startDate: startDate || timeDifference,
                endDate: endDate || currentDateTime,
              };
              search(offset, searchParams);
            }}
          />
        )}
      </Box>
    </Box>
  );
};

export default FileConversionTable;
